import { useState, useEffect, useRef } from 'react'
import ConfirmDialog from '@/components/ui/ConfirmDialog/ConfirmDialog'

/**
 * Hook pour gérer la confirmation de fermeture d'un formulaire modifié
 *
 * @param {Object} options - Options de configuration
 * @param {boolean} options.isDirty - État indiquant si le formulaire a été modifié
 * @param {boolean} options.fieldsToWatch - Liste des champs à surveiller pour juger de l'état du formulaire
 * @param {Function} watch - Fonction watch issue useForm de react-hook-form
 * @param {Function} getValues - Fonction pour lire les values du formulaire
 * @param {Function} options.onClose - Fonction à appeler pour reset le formulaire avant fermeture
 * @param {Function} options.onClose - Fonction à appeler lors de la fermeture
 * @param {Object} options.messages - Messages personnalisés pour la boîte de dialogue
 * @returns {Object} - Fonctions pour gérer la confirmation et le composant de dialogue
 */
const useFormConfirmation = ({
  isDirty = null,
  fieldsToWatch = null,
  watch,
  getValues,
  onReset = () => {},
  onClose = () => {},
  messages = {
    title: "Attention !!",
    message: "Vous avez demandé à fermer le formulaire, mais des données ont été modifiées.",
    subMessage: "Vos modifications seront perdues si vous ne les enregistrez pas.",
    confirmButton: "Ne pas enregistrer",
    cancelButton: "Revenir en arrière"
  }
}) => {
  const [showConfirmDialog, setShowConfirmDialog] = useState(false)
  const [initialLoadComplete, setInitialLoadComplete] = useState(false)
  const [initialValues, setInitialValues] = useState({})

  // Utiliser uniquement useRef pour l'état dirty afin d'éviter les re-rendus
  const isDirtyRef = useRef(false)
  // Référence pour stocker les champs à surveiller et éviter les re-rendus inutiles
  const fieldsToWatchRef = useRef(fieldsToWatch)

  const isDirtyFromProps = isDirty !== null

  // Mettre à jour la référence des champs à surveiller sans déclencher l'effet
  useEffect(() => {
    fieldsToWatchRef.current = fieldsToWatch
  }, [fieldsToWatch])

  // useFormConfirmation feature
  // Surveiller les changements de valeurs après le chargement initial
  useEffect(() => {
    if(isDirtyFromProps) return;
    if (!initialLoadComplete) return;
    
    const subscription = watch((values) => {
      // Comparer les valeurs actuelles avec les valeurs initiales
      const fieldsToCheck = fieldsToWatchRef.current ?
        fieldsToWatchRef.current :
        Object.keys(values)
      
      let newIsDirty = false
      
      for (const field of fieldsToCheck) {
        // Vérifier si le champ existe dans les valeurs initiales et actuelles
        if (field in initialValues && field in values) {
          // Comparer les valeurs (gestion spéciale pour les objets et tableaux)
          if (typeof values[field] === 'object' && values[field] !== null) {
            // Pour les objets et tableaux, comparer en JSON
            if (JSON.stringify(values[field]) !== JSON.stringify(initialValues[field])) {
              newIsDirty = true
              break
            }
          } else if (values[field] !== initialValues[field]) {
            newIsDirty = true
            break
          }
        }
      }
      // Mettre à jour uniquement la référence sans provoquer de re-rendu
      isDirtyRef.current = newIsDirty
    })
    
    // Nettoyer l'abonnement
    return () => {
      subscription.unsubscribe()
    }
  }, [initialLoadComplete, initialValues, watch])

  const handleFormCancel = () => {
    // Utiliser uniquement isDirtyRef.current pour vérifier l'état du formulaire
    if ((isDirtyFromProps && !isDirty) || (!isDirtyFromProps && !isDirtyRef.current)) {
      onReset()

      if(!isDirtyFromProps) {
        setInitialValues(getValues())
        isDirtyRef.current = false
      }

      onClose()
    } else {
      setShowConfirmDialog(true)
    }
  }

  const handleConfirmClose = () => {
    setShowConfirmDialog(false)
    onReset()
    onClose()
  }

  const handleCancelClose = () => {
    setShowConfirmDialog(false)
  }

  // Fonction qui retourne le composant de dialogue
  const FormConfirmDialog = () => (
    <ConfirmDialog
      show={showConfirmDialog}
      onConfirm={handleConfirmClose}
      onCancel={handleCancelClose}
      messages={messages}
    />
  )

  return {
    handleFormCancel,
    setInitialLoadComplete,
    setInitialValues,
    // Fournir une fonction setFormIsDirty pour la compatibilité, mais qui met à jour uniquement la référence
    setFormIsDirty: (value) => {
      isDirtyRef.current = value
    },
    FormConfirmDialog
  }
}

export default useFormConfirmation